/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

import React from 'react';
import FieldWrapper from '../common/FieldWrapper';
import ThemedButton from '../common/ThemedButton';
import ThemedTextArea from '../common/ThemedTextArea';
import ThemedToggle from '../common/ThemedToggle';
import {
  ImageField, inlineButton, LongTextField, PointField, Row, Section, SelectField, TextField,
} from './EventFormFields';
import { groupedTimezoneOptions } from './canonicalTimezones';
import { formatDateTime, formatDateTimeRange } from './formatDateTime';
import { toOptions as asOptions } from './lookupOptions';
import { MAX_ACTIVITIES_PER_EVENT, MAX_PARTICIPANT_NOTE_LENGTH } from './validateEventForm';

/**
 * The wizard's steps. Presentational only — every step receives the form state
 * and a dispatch, so the same components can back an Edit screen later.
 */

const CREATOR_TYPES = [
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'USER', label: 'User' },
];
const ORGANIZER_TYPES = [
  { value: 'PLATFORM', label: 'Platform' },
  { value: 'EXTERNAL', label: 'External' },
  { value: 'USER', label: 'User' },
];
const ACTIVITY_MODES = [
  { value: 'DEFAULT', label: 'Default — a single activity' },
  { value: 'PARALLEL', label: 'Parallel — participants pick one' },
  { value: 'SEQUENTIAL', label: 'Sequential — legs run in order' },
];
// D-54: this column says only WHERE a signup is taken. "Open to all" was
// removed from it because it answers a different question — one already
// answered by the "Registration required" toggle and the registration type.
const REGISTRATION_SOURCES = [
  { value: 'PLATFORM', label: 'On Platform' },
  { value: 'EXTERNAL', label: 'On the organizer’s own site' },
];
const LOCATION_MODELS = [
  { value: 'ROUTE_BASED', label: 'Route — start, finish and distance' },
  { value: 'VENUE_BASED', label: 'Venue — a single location' },
];


const COVER_IMAGE_HINT =
  'Recommended: 1600 \u00d7 400 px (4:1). The public event page shows it as a wide '
  + '1440 \u00d7 360 banner and crops it to fill, so keep the subject near the centre. '
  + 'JPG, PNG or WebP, up to 1 MB.';

const SPONSOR_LOGO_HINT =
  'Recommended: a square at least 128 \u00d7 128 px. It is scaled down to fit, never '
  + 'cropped, so any shape works \u2014 a transparent PNG sits best on the page. '
  + 'Up to 1 MB.';

const SPONSOR_COVER_HINT =
  'Stored for later \u2014 the public event page shows only the logo today, so no '
  + 'particular size is needed. JPG, PNG or WebP, up to 1 MB.';

/**
 * Timezone options, canonicalised so the stored value does not depend on which
 * machine's ICU build answered. See canonicalTimezones.js.
 */
// Grouped by region with the offset alongside, and the browser's own zone
// offered first. Built once: 418 zones is enough that rebuilding it on every
// render is wasted work, and the offsets do not change while a form is open.
const TIMEZONES = groupedTimezoneOptions();



// ── Step 1 — Basics ─────────────────────────────────────────────────────────

export function EventBasicsStep({
  form, errors, dispatch, lookups, onAddOrganization, isEdit,
  organizationId, applicationId,
}) {
  const setField = (field) => (value) => dispatch({ type: 'setField', field, value });

  return (
    <Section title="Basic information" subtitle="What the event is and who is running it.">
      <TextField
        label="Title" required error={errors.title}
        value={form.title} onChange={setField('title')}
        placeholder="Bangalore 10K 2026"
        hint={isEdit ? undefined : 'The event\u2019s web address is built from this title when you save.'}
      />
      <FieldWrapper label="Description" required error={errors.description}>
        <ThemedTextArea
          rows={5}
          error={errors.description}
          value={form.description}
          placeholder="What participants should know about the event."
          onChange={(e) => dispatch({ type: 'setField', field: 'description', value: e.target.value })}
        />
      </FieldWrapper>

      <LongTextField
        label="Prerequisites" error={errors.prerequisites}
        value={form.prerequisites}
        onChange={setField('prerequisites')}
        rows={4}
        maxLength={MAX_PARTICIPANT_NOTE_LENGTH}
        placeholder="What someone must already have or meet to take part — a minimum age, a fitness certificate, their own bike."
        hint="Optional. Shown to participants before they register."
      />
      <LongTextField
        label="Preparation steps" error={errors.preparation_steps}
        value={form.preparation_steps}
        onChange={setField('preparation_steps')}
        rows={4}
        maxLength={MAX_PARTICIPANT_NOTE_LENGTH}
        placeholder="What to do or bring on the day — collect the bib the evening before, carry a photo ID, arrive by 5:30 am."
        hint="Optional. Shown to participants before they register."
      />

      <Row>
        <SelectField
          label="Created by" required error={errors.creator_type}
          value={form.creator_type} onChange={setField('creator_type')} options={CREATOR_TYPES}
        />
        <SelectField
          label="Hosted by" required error={errors.organizer}
          value={form.organizer} onChange={setField('organizer')} options={ORGANIZER_TYPES}
        />
      </Row>

      {form.creator_type === 'ORGANIZATION' && (
        <>
          <SelectField
            label="Organization" required error={errors.creator_organization_id}
            value={form.creator_organization_id}
            onChange={setField('creator_organization_id')}
            options={asOptions(lookups.organizations)}
            placeholder="Choose the organization…"
          />
          {onAddOrganization && (
            <div style={{ marginTop: -10, marginBottom: 18, fontSize: 13 }}>
              Not listed?{' '}
              <button
                type="button"
                onClick={onAddOrganization}
                style={{
                  border: 'none', background: 'transparent', padding: 0,
                  color: '#4338ca', textDecoration: 'underline', cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                Add an organization
              </button>
            </div>
          )}
        </>
      )}

      {form.creator_type === 'USER' && (
        <TextField
          label="Creator user id" required type="number" error={errors.creator_user_id}
          value={form.creator_user_id} onChange={setField('creator_user_id')}
          hint="The event table stores this as a plain id — there is no user picker to read from."
        />
      )}

      <Row>
        {/* The slug is minted by the platform from the title on insert and never
            changes afterwards, so links already shared keep resolving. There is
            nothing to type when creating; on an existing event it is shown
            read-only, because it is the event's public address and an admin
            needs to be able to read it. */}
        {isEdit && (
          <TextField
            label="Web address (slug)"
            value={form.slug}
            onChange={() => {}}
            disabled
            hint="Set when the event was created. It never changes, so links already shared keep working."
          />
        )}
        <TextField
          label="Official website" error={errors.official_website_url}
          value={form.official_website_url} onChange={setField('official_website_url')}
          placeholder="https://…"
        />
      </Row>
      <ImageField
        label="Cover image" required error={errors.cover_image_url}
        value={form.cover_image_url} onChange={setField('cover_image_url')}
        name="cover_image_url"
        hint={COVER_IMAGE_HINT}
        organizationId={organizationId} applicationId={applicationId}
      />
    </Section>
  );
}

// ── Step 2 — Schedule ───────────────────────────────────────────────────────

export function EventScheduleStep({ form, errors, dispatch }) {
  const setField = (field) => (value) => dispatch({ type: 'setField', field, value });

  return (
    <Section
      title="Schedule and location"
      subtitle="Times are stored against the timezone you choose, not the browser’s."
    >
      <Row>
        <TextField
          label="Location" required error={errors.location}
          value={form.location} onChange={setField('location')}
          placeholder="Bangalore, Karnataka"
        />
        <SelectField
          label="Timezone" required error={errors.timezone}
          value={form.timezone} onChange={setField('timezone')} options={TIMEZONES}
          placeholder="Choose a timezone…"
        />
      </Row>
      <Row>
        <TextField
          label="State" required error={errors.location_state}
          value={form.location_state} onChange={setField('location_state')}
          placeholder="Karnataka"
        />
        <TextField
          label="Country" required error={errors.location_country}
          value={form.location_country} onChange={setField('location_country')}
          placeholder="India"
        />
        <TextField
          label="PIN code" error={errors.location_pincode}
          value={form.location_pincode} onChange={setField('location_pincode')}
          placeholder="540001"
        />
      </Row>
      <Row>
        <TextField
          label="Starts at" required type="datetime-local" error={errors.start_datetime}
          value={form.start_datetime} onChange={setField('start_datetime')}
        />
        <TextField
          label="Ends at" required type="datetime-local" error={errors.end_datetime}
          value={form.end_datetime} onChange={setField('end_datetime')}
        />
      </Row>
      <SelectField
        label="How the activities run" required error={errors.activity_mode}
        value={form.activity_mode} onChange={setField('activity_mode')} options={ACTIVITY_MODES}
      />
    </Section>
  );
}

// ── Step 3 — Activities ─────────────────────────────────────────────────────

function PitStops({ activity, index, dispatch, error }) {
  return (
    <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: '3px solid #e5e7eb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Pit stops</div>
        <ThemedButton
          type="button" variant="outlined" style={inlineButton}
          onClick={() => dispatch({ type: 'addPitStop', index })}
        >
          Add pit stop
        </ThemedButton>
      </div>
      {error && <div style={{ color: '#d53b3b', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      {(activity.pit_stops || []).map((stop, stopIndex) => (
        <div key={stopIndex} style={{ marginBottom: 10 }}>
          <Row>
            <TextField
              label={`Stop ${stopIndex + 1} name`}
              value={stop.name}
              onChange={(value) => dispatch({ type: 'updatePitStop', index, stopIndex, field: 'name', value })}
              placeholder="Water station 1"
            />
            <PointField
              label="Location"
              point={stop.point}
              onAxisChange={(axis, value) => dispatch({ type: 'updatePitStopPoint', index, stopIndex, axis, value })}
            />
          </Row>
          <ThemedButton
            type="button" variant="outlined" style={inlineButton}
            onClick={() => dispatch({ type: 'removePitStop', index, stopIndex })}
          >
            Remove stop
          </ThemedButton>
        </div>
      ))}
    </div>
  );
}

export function EventActivitiesStep({ form, errors, dispatch, lookups }) {
  const activityErrors = errors.activityErrors || [];
  const atCap = form.activities.length >= MAX_ACTIVITIES_PER_EVENT;

  return (
    <Section
      title="Activities"
      subtitle={`The categories or legs participants choose between. At most ${MAX_ACTIVITIES_PER_EVENT}.`}
      actions={(
        <ThemedButton
          type="button" style={inlineButton} disabled={atCap}
          onClick={() => dispatch({ type: 'addActivity' })}
        >
          Add activity
        </ThemedButton>
      )}
    >
      {errors.activities && (
        <div style={{ color: '#d53b3b', fontSize: 13, marginBottom: 12 }}>{errors.activities}</div>
      )}

      {form.activities.map((activity, index) => {
        const activityError = activityErrors[index] || {};
        return (
          <div
            key={index}
            style={{
              border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginBottom: 16,
              background: '#fafafa',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontWeight: 600 }}>Activity {index + 1}</div>
              <ThemedButton
                type="button" variant="outlined" style={inlineButton}
                onClick={() => dispatch({ type: 'removeActivity', index })}
              >
                Remove
              </ThemedButton>
            </div>

            <Row>
              <TextField
                label="Name" required error={activityError.name}
                value={activity.name}
                onChange={(value) => dispatch({ type: 'updateActivity', index, field: 'name', value })}
                placeholder="10K"
              />
              <SelectField
                label="Activity type" required error={activityError.activity_type_id}
                value={activity.activity_type_id}
                onChange={(value) => dispatch({ type: 'updateActivity', index, field: 'activity_type_id', value })}
                options={asOptions(lookups.activityTypes)}
              />
            </Row>

            <SelectField
              label="Location model" required error={activityError.location_model}
              value={activity.location_model}
              onChange={(value) => dispatch({ type: 'updateActivity', index, field: 'location_model', value })}
              options={LOCATION_MODELS}
            />

            {activity.location_model === 'ROUTE_BASED' && activity.route && (
              <>
                {activityError.route && (
                  <div style={{ color: '#d53b3b', fontSize: 13, marginBottom: 8 }}>{activityError.route}</div>
                )}
                <Row>
                  <TextField
                    label="Start address" required
                    value={activity.route.start_address}
                    onChange={(value) => dispatch({ type: 'updateActivityNested', index, group: 'route', field: 'start_address', value })}
                  />
                  <TextField
                    label="Finish address" required
                    value={activity.route.end_address}
                    onChange={(value) => dispatch({ type: 'updateActivityNested', index, group: 'route', field: 'end_address', value })}
                  />
                </Row>
                <Row>
                  <PointField
                    label="Start point" required point={activity.route.start_point}
                    onAxisChange={(axis, value) => dispatch({ type: 'updateActivityPoint', index, group: 'route', field: 'start_point', axis, value })}
                  />
                  <PointField
                    label="Finish point" required point={activity.route.end_point}
                    onAxisChange={(axis, value) => dispatch({ type: 'updateActivityPoint', index, group: 'route', field: 'end_point', axis, value })}
                  />
                </Row>
                <Row>
                  <TextField
                    label="Total distance" required type="number" step="any"
                    value={activity.route.total_distance}
                    onChange={(value) => dispatch({ type: 'updateActivityNested', index, group: 'route', field: 'total_distance', value })}
                  />
                  <SelectField
                    label="Measured in" required
                    value={activity.route.distance_measuring_unit_id}
                    onChange={(value) => dispatch({ type: 'updateActivityNested', index, group: 'route', field: 'distance_measuring_unit_id', value })}
                    options={asOptions(lookups.distanceUnits)}
                  />
                </Row>
                <PitStops activity={activity} index={index} dispatch={dispatch} error={activityError.pit_stops} />
              </>
            )}

            {activity.location_model === 'VENUE_BASED' && activity.venue && (
              <>
                {activityError.venue && (
                  <div style={{ color: '#d53b3b', fontSize: 13, marginBottom: 8 }}>{activityError.venue}</div>
                )}
                <Row>
                  <TextField
                    label="Venue name" required
                    value={activity.venue.venue_name}
                    onChange={(value) => dispatch({ type: 'updateActivityNested', index, group: 'venue', field: 'venue_name', value })}
                  />
                  <TextField
                    label="Venue address" required
                    value={activity.venue.venue_address}
                    onChange={(value) => dispatch({ type: 'updateActivityNested', index, group: 'venue', field: 'venue_address', value })}
                  />
                </Row>
                <PointField
                  label="Venue location" required point={activity.venue.venue_point}
                  onAxisChange={(axis, value) => dispatch({ type: 'updateActivityPoint', index, group: 'venue', field: 'venue_point', axis, value })}
                />
              </>
            )}
          </div>
        );
      })}
    </Section>
  );
}

// ── Step 4 — Registration ───────────────────────────────────────────────────

export function EventRegistrationStep({ form, errors, dispatch, lookups }) {
  const setField = (field) => (value) => dispatch({ type: 'setField', field, value });
  const setPlatform = (field) => (value) => dispatch({
    type: 'updateNested', group: 'platform_registration', field, value,
  });
  const setExternal = (field) => (value) => dispatch({
    type: 'updateNested', group: 'external_registration', field, value,
  });

  return (
    <Section
      title="Registration"
      subtitle="The window decides when registration opens and closes — the platform moves the status on a clock."
    >
      <Row>
        <SelectField
          label="Where people register" required error={errors.registration_source}
          value={form.registration_source} onChange={setField('registration_source')}
          options={REGISTRATION_SOURCES}
        />
        <SelectField
          label="Registration type" required error={errors.registration_type_id}
          value={form.registration_type_id} onChange={setField('registration_type_id')}
          options={asOptions(lookups.registrationTypes)}
        />
      </Row>

      <FieldWrapper label="Registration required">
        <ThemedToggle
          checked={!!form.is_registration_required}
          onChange={(value) => dispatch({ type: 'setField', field: 'is_registration_required', value })}
        />
      </FieldWrapper>

      {form.registration_source === 'PLATFORM' && form.platform_registration && (
        <>
          <Row>
            <TextField
              label="Registration opens" required type="datetime-local"
              error={errors.registration_start_datetime}
              value={form.platform_registration.registration_start_datetime}
              onChange={setPlatform('registration_start_datetime')}
            />
            <TextField
              label="Registration closes" required type="datetime-local"
              error={errors.registration_end_datetime}
              value={form.platform_registration.registration_end_datetime}
              onChange={setPlatform('registration_end_datetime')}
            />
          </Row>
          <TextField
            label="Maximum participants" type="number" min="1"
            error={errors.max_participants}
            value={form.platform_registration.max_participants}
            onChange={setPlatform('max_participants')}
            hint="The most people who may register. Leave empty for no limit."
          />
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            Registration is free this phase — the fee is stored as zero.
          </div>
        </>
      )}

      {form.registration_source === 'EXTERNAL' && form.external_registration && (
        <>
          <TextField
            label="Registration URL" required error={errors.external_url}
            value={form.external_registration.external_url}
            onChange={setExternal('external_url')}
            placeholder="https://…"
          />
          <Row>
            <TextField
              label="Opens" type="datetime-local"
              value={form.external_registration.registration_start_date}
              onChange={setExternal('registration_start_date')}
            />
            <TextField
              label="Closes" type="datetime-local"
              value={form.external_registration.registration_end_date}
              onChange={setExternal('registration_end_date')}
            />
          </Row>
        </>
      )}
    </Section>
  );
}

// ── Step 5 — Sponsors ───────────────────────────────────────────────────────

export function EventSponsorsStep({ form, dispatch, organizationId, applicationId }) {
  return (
    <Section
      title="Sponsors"
      subtitle="Optional. Shown on the public event page in this order."
      actions={(
        <ThemedButton type="button" style={inlineButton} onClick={() => dispatch({ type: 'addSponsor' })}>
          Add sponsor
        </ThemedButton>
      )}
    >
      {!form.sponsors.length && (
        <div style={{ fontSize: 14, color: '#6b7280' }}>No sponsors added.</div>
      )}
      {form.sponsors.map((sponsor, index) => (
        <div key={index} style={{ borderBottom: '1px solid #eee', paddingBottom: 10, marginBottom: 10 }}>
          <Row>
            <TextField
              label={`Sponsor ${index + 1}`} required
              value={sponsor.name}
              onChange={(value) => dispatch({ type: 'updateSponsor', index, field: 'name', value })}
            />
            <TextField
              label="Website"
              value={sponsor.website_url}
              onChange={(value) => dispatch({ type: 'updateSponsor', index, field: 'website_url', value })}
              placeholder="https://…"
            />
          </Row>
          <Row>
            <ImageField
              label="Logo"
              value={sponsor.logo_url}
              onChange={(value) => dispatch({ type: 'updateSponsor', index, field: 'logo_url', value })}
              name={`sponsor_logo_${index}`}
              hint={SPONSOR_LOGO_HINT}
              organizationId={organizationId} applicationId={applicationId}
            />
            <ImageField
              label="Cover image"
              value={sponsor.cover_image}
              onChange={(value) => dispatch({ type: 'updateSponsor', index, field: 'cover_image', value })}
              name={`sponsor_cover_${index}`}
              hint={SPONSOR_COVER_HINT}
              organizationId={organizationId} applicationId={applicationId}
            />
          </Row>
          <ThemedButton
            type="button" variant="outlined" style={inlineButton}
            onClick={() => dispatch({ type: 'removeSponsor', index })}
          >
            Remove sponsor
          </ThemedButton>
        </div>
      ))}
    </Section>
  );
}

// ── Step 6 — Review ─────────────────────────────────────────────────────────

export function EventReviewStep({ form, operationCount }) {
  const REGISTRATION_LABELS = {
    PLATFORM: 'On Platform',
    EXTERNAL: 'On the organizer’s own site',
  };
  const registration = form.platform_registration;

  const rows = [
    ['Title', form.title],
    ['When', formatDateTimeRange(form.start_datetime, form.end_datetime)],
    ['Timezone', form.timezone],
    ['Location', form.location],
    ['State', form.location_state],
    ['Country', form.location_country],
    ['PIN code', form.location_pincode],
    ['Activities', String(form.activities.length)],
    ['Sponsors', String(form.sponsors.length)],
    ['Registration', REGISTRATION_LABELS[form.registration_source] || form.registration_source],
    registration && ['Registration opens', formatDateTime(registration.registration_start_datetime)],
    registration && ['Registration closes', formatDateTime(registration.registration_end_datetime)],
    registration && ['Maximum participants',
      registration.max_participants ? String(registration.max_participants) : 'No limit'],
    // Both are free text long enough to swamp a summary table, so the review
    // reports only whether one was written — the field itself is a step back.
    ['Prerequisites', form.prerequisites ? 'Provided' : 'None'],
    ['Preparation steps', form.preparation_steps ? 'Provided' : 'None'],
    ['Status on save', form.status === 'PUBLISHED' ? 'Published' : 'Draft'],
  ].filter(Boolean);

  return (
    <Section
      title="Review"
      subtitle={`Saved as one transaction — ${operationCount} operations. If any part fails, nothing is written.`}
    >
      <table style={{ width: '100%', fontSize: 14 }}>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <td style={{ padding: '6px 12px 6px 0', color: '#6b7280', width: 180 }}>{label}</td>
              <td style={{ padding: '6px 0', fontWeight: 500 }}>{value || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {form.status === 'PUBLISHED' && form.registration_source === 'PLATFORM' && (
        <div style={{ marginTop: 14, fontSize: 13, color: '#6b7280' }}>
          Registration opens by itself when the window starts — you do not set that status by hand.
        </div>
      )}
    </Section>
  );
}
